import type { BattleRules, CardId } from '../engine/types';
import type { ClaimStatus, FinalClaim, IceServerConfig, InvitePreview, LeaderboardEntry, NetworkIdentity, RoomSession } from './contracts';
import { NETWORK_PROTOCOL_VERSION } from './contracts';

interface ApiError { error?: string }

export class NetworkApiClient {
  constructor(private readonly baseUrl: string) {}

  async ensureIdentity(displayName: string, existing: NetworkIdentity | null): Promise<NetworkIdentity> {
    return this.request<NetworkIdentity>('/v1/players', {
      method: 'POST',
      body: { displayName },
      ...(existing ? { ticket: existing.playerTicket } : {}),
    });
  }

  async createRoom(identity: NetworkIdentity, input: { displayName: string; engineVersion: string; contentVersion: string; arenaId: string; rules: BattleRules; deck: readonly CardId[] }): Promise<RoomSession> {
    return this.request<RoomSession>('/v1/rooms', { method: 'POST', ticket: identity.playerTicket, body: { protocolVersion: NETWORK_PROTOCOL_VERSION, ...input } });
  }

  async joinRoom(identity: NetworkIdentity, input: { roomCode: string; displayName: string; engineVersion: string; contentVersion: string; deck: readonly CardId[] }): Promise<RoomSession> {
    return this.request<RoomSession>('/v1/rooms/join', { method: 'POST', ticket: identity.playerTicket, body: { protocolVersion: NETWORK_PROTOCOL_VERSION, ...input } });
  }

  async joinInvite(identity: NetworkIdentity, input: { inviteToken: string; displayName: string; engineVersion: string; contentVersion: string; deck: readonly CardId[] }): Promise<RoomSession> {
    return this.request<RoomSession>('/v1/invites/join', { method: 'POST', ticket: identity.playerTicket, body: { protocolVersion: NETWORK_PROTOCOL_VERSION, ...input } });
  }

  async invitePreview(inviteToken: string, identity?: NetworkIdentity): Promise<InvitePreview> {
    return this.request<InvitePreview>(identity ? '/v1/invites/preview' : '/v1/invites/social', { method: 'POST', ...(identity ? { ticket: identity.playerTicket } : {}), body: { inviteToken } });
  }

  async iceServers(identity: NetworkIdentity): Promise<IceServerConfig> {
    return this.request<IceServerConfig>('/v1/ice-servers', { method: 'GET', ticket: identity.playerTicket });
  }

  async submitClaim(session: RoomSession, claim: FinalClaim): Promise<ClaimStatus> {
    return this.request<ClaimStatus>(`/v1/rooms/${encodeURIComponent(session.roomId)}/claim`, { method: 'POST', ticket: session.seatTicket, body: claim });
  }

  async abandonRoom(session: RoomSession): Promise<void> {
    await this.request<{ ok: boolean }>(`/v1/rooms/${encodeURIComponent(session.roomId)}/abandon`, { method: 'POST', ticket: session.seatTicket, keepalive: true });
  }

  async leaderboard(): Promise<readonly LeaderboardEntry[]> {
    const response = await this.request<{ entries: LeaderboardEntry[] }>('/v1/leaderboard/network', { method: 'GET' });
    return response.entries;
  }

  private async request<T>(path: string, options: { method: 'GET' | 'POST'; body?: unknown; ticket?: string; keepalive?: boolean }): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: options.method,
      headers: { ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }), ...(options.ticket ? { Authorization: `Bearer ${options.ticket}` } : {}) },
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.keepalive ? { keepalive: true } : {}),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({})) as ApiError;
      throw new Error(error.error ?? `HTTP ${response.status}`);
    }
    return await response.json() as T;
  }
}

export function configuredNetworkBaseUrl(): string {
  const configured = import.meta.env.VITE_REALTIME_URL as string | undefined;
  return (configured?.replace(/\/$/, '') || '/api/network');
}
